import {
  getMessages,
  getMessage,
  deleteMessages,
  duplicateMessage,
} from '../services/MessageApiHelper';
import {
  convertNumberToCursor,
  convertCursorToNumber,
} from '../utils';
import { loading } from './LoadingUtil';
import { formatDate, TimeFormater } from '../utils/TimeFormatUtil';
import {
  LanguageConfig,
  MessageChannel,
  MessageTag,
  APIStatus,
} from '../config/CustomEnums';

const getInitialState = () => ({
  messageList: [],
  listDisplayFields: [
    { displayName: 'ID', fieldName: 'pk'  },
    { displayName: 'Name', fieldName: 'name', linked: true, orderField: "title" },
    { displayName: 'Message channel', fieldName: 'displayChannels' },
    {
      displayName: 'Related campaign',
      fieldName: 'relatedCampaignName',
      orderField: 'campaign',
    },
    { displayName: 'Target customers', fieldName: 'displayTargetCustomer' },
    {
      displayName: 'Delivery time',
      fieldName: 'deliveryDate',
      orderField: 'scheduledDate',
    },
    { displayName: 'Status', fieldName: 'status' },
  ],
  currentPageMessageList: [],
  pageInfo: {
    startCursor: '',
    endCursor: '',
    hasNextPage: false,
    hasPreviousPage: false,
  },
  currentLastCursor: '',
  currentPage: 0,
  totalPage: 0,
  totalCount: 0,
  checkedList: [],
  message: {},
  errorFields: [],
});

const parseChannel = (channelName) => {
  if (channelName === 'INBOX') {
    return MessageChannel.inbox;
  }
  if (channelName === 'PUSH_NOTIFICATION') {
    return MessageChannel.push;
  }
};

const parseMessageTranslations = (translations) => {
  let parsedTranslations = {};
  translations.forEach((item) => {
    const translationData = item.node;
    let language = translationData.language;
    if (language === 'ZH_HANS') {
      language = LanguageConfig.simplifiedChinese;
    } else if (language === 'ZH_HANT') {
      language = LanguageConfig.traditionalChinese;
    }
    parsedTranslations[language] = {
      name: translationData.title,
      content: translationData.content,
      photo: translationData.photo,
      id: translationData.id,
      pk: translationData.pk,
    };
  });
  return parsedTranslations;
};

const parseMessageStatus = (sendStatus, isDraft) => {
  switch (sendStatus) {
    case 'STATE_COMPLETED':
      return MessageTag.sent;
    case 'STATE_INITIAL':
      if (isDraft) {
        return MessageTag.saved;
      }
      return MessageTag.scheduled;
    case 'STATE_SENDING':
      return MessageTag.sending;
    case 'STATE_ERROR':
      return MessageTag.error;
    default:
      break;
  }
};

const parseListMessage = (data) => {
  const parsedChannelList = data.channels.edges?.map((item) =>
    parseChannel(item.node.pk),
  );
  const customerGroupList = data.targetedCustomerGroups?.edges?.map(
    (item) => item.node.name,
  );
  const segmentsNameList = data.targetedSegments?.edges?.map(
    (item) => item.node?.name,
  );
  const translations = data.translations?.edges?.length > 0
    ? parseMessageTranslations(data.translations.edges)
    : {};
  return {
    pk: data.pk,
    id: data.id,
    name: data.title,
    content: data.content,
    photo: data.photo,
    channels: parsedChannelList,
    displayChannels: parsedChannelList.toString(),
    relatedCampaign: {
      pk: data.campaign?.pk,
      name: data.campaign?.name,
    },
    relatedCampaignName: data.campaign?.name,
    targetCustomerGroup: data.targetedCustomerGroups?.edges.map(
      (item) => item.node,
    ),
    displayTargetCustomer: customerGroupList.toString(),
    targetedSegments: data.targetedSegments?.edges.map((item) => item.node),
    displayTargetedSegments:
      segmentsNameList?.length > 0 ? segmentsNameList.toString() : null,
    scheduledDate: data.scheduledDate,
    deliveryDate: formatDate(data.scheduledDate, TimeFormater.dayMonthYearWeekTimeA),
    translations: translations,
    status: parseMessageStatus(data.sendingState, data.isDraft),
  };
};

const parseDetailMessage = (data) => {
  const parsedChannelList = data.channels.edges?.map((item) =>
    parseChannel(item.node.pk),
  );
  const translations = {
    [LanguageConfig.english]: {
      name: data.title,
      content: data.content,
      photo: data.photo != null && data.photo !== ''
        ? {
          type: 'TYPE_URL',
          value: data.photo
        }
        : null,
    },
  };
  const edges = data.translations?.edges || [];
  edges.forEach((translation) => {
    const { language, title, content, photo, pk, id } = translation.node;
    translations[language] = {
      name: title,
      content: content,
      photo: photo != null && photo !== ''
        ? {
          type: 'TYPE_URL',
          value: photo
        }
        : null,
      id: id,
      pk: pk,
    };
  });
  return {
    pk: data.pk,
    id: data.id,
    name: data.title,
    content: data.content,
    photo: data.photo,
    channels: parsedChannelList,
    displayChannels: parsedChannelList.toString(),
    relatedCampaign: {
      pk: data.campaign?.pk,
      name: data.campaign?.name,
    },
    targetCustomerGroup: data.targetedCustomerGroups?.edges.map((item) => item.node),
    targetedSegments: data.targetedSegments?.edges.map((item) => item.node),
    scheduledDate: data.scheduledDate,
    translations: translations,
    status: parseMessageStatus(data.sendingState, data.isDraft),
  };
}

export default {
  namespace: 'messageList',
  state: getInitialState(),
  reducers: {
    updateState(state, { payload }) {
      return {
        ...state,
        ...payload,
      };
    },
    clearMessageState(state, { payload }) {
      return {
        ...state,
        message: {},
      };
    },
  },
  effects: {
    getCurrentPageMessages: [
      function* ({ payload }, { put }) {
        const { page, rank, searchKey } = payload;
        let afterCursor = '';
        if (page > 1) {
          afterCursor = convertNumberToCursor((page - 1) * 20 - 1);
        }
        const serviceArgs = [
          getMessages,
          afterCursor,
          rank,
          searchKey,
          payload,
        ];
        function* onSuccess(data) {
          const pageInfo = data.messages.pageInfo;
          const totalCount = data.messages.totalCount;
          const currentLastCursor = pageInfo.endCursor;
          const messageData = data.messages.edges;
          const messageList = messageData.map((item) =>
            parseListMessage(item.node),
          );
          yield put({
            type: 'updateState',
            payload: {
              currentPageMessageList: messageList,
              pageInfo: {
                startCursor: convertCursorToNumber(pageInfo?.startCursor) + 1,
                endCursor: convertCursorToNumber(pageInfo?.endCursor) + 1,
              },
              totalCount,
              currentLastCursor,
              totalPage: Math.ceil(totalCount / 20),
            }
          });
        }
        yield loading(serviceArgs, onSuccess);
      },
      { type: 'takeLatest' },
    ],
    getMessage: [
      function* ({ payload }, { put }) {
        const { messagePK } = payload;
        const messageID = btoa(`MessageNode:${messagePK}`);
        const serviceArgs = [getMessage, messageID];
        yield put({
          type: 'createMessage/updateState',
          payload: {
            createStatus: APIStatus.calling,
          }
        });
        function* onSuccess(data) {
          const messageData = data.message;
          const message = parseDetailMessage(messageData);
          yield put({
            type: 'updateState',
            payload: {
              message: message,
            }
          });
          yield put({
            type: 'createMessage/updateState',
            payload: {
              message: message,
              createStatus: APIStatus.success,
              hasUpdatedDefaultValues: true,
            }
          });
          const afterAction = payload.afterAction || (() => {});
          yield afterAction();
        }
        yield loading(serviceArgs, onSuccess);
      },
      { type: 'takeLatest' },
    ],
    delete: [
      function* ({ payload }, { put, select }) {
        const { checkedList } = yield select((state) => ({
          checkedList: state.messageList.checkedList,
        }));

        let pks = [];
        checkedList.forEach((item) => {
          pks.push(item.pk);
        });

        const serviceArgs = [deleteMessages, pks];
        function* onSuccess() {
          const afterActions = payload.afterAction;
          yield afterActions();
        }
        yield loading(serviceArgs, onSuccess);
      },
      { type: 'takeLatest' },
    ],
    duplicate: [
      function* ({ payload }, { put }) {
        const serviceArgs = [duplicateMessage, payload.data.pk];
        function* onSuccess() {
          const afterActions = payload.afterAction || (() => {});
          yield afterActions();
        }
        yield loading(serviceArgs, onSuccess);
      },
      { type: 'takeLatest' },
    ],
  },
};
